import { z } from "zod";

/**
 * Calendar day in ISO `YYYY-MM-DD` form.
 *
 * Every date in this system is a plain calendar day, never a `Date`. Market
 * data is inherently daily, and `Date` drags a timezone into JSON round-trips
 * that can silently shift a bar by one session depending on the reader's
 * offset. A string that only ever means "this trading day" cannot do that.
 */
export const IsoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "expected YYYY-MM-DD")
  .brand<"IsoDate">();
export type IsoDate = z.infer<typeof IsoDate>;

/** Instant, ISO 8601 with offset. Used for news and generation timestamps. */
export const IsoInstant = z.string().datetime({ offset: true });

/**
 * Ticker symbol, normalized to uppercase.
 *
 * Providers disagree on case and on separators for share classes (BRK.B vs
 * BRK-B), so normalization happens here, once, at the schema boundary.
 */
export const Symbol_ = z
  .string()
  .min(1)
  .max(12)
  .transform((s) => s.toUpperCase())
  .pipe(z.string().regex(/^[A-Z0-9.\-]+$/, "invalid ticker characters"))
  .brand<"Symbol">();
export type Symbol_ = z.infer<typeof Symbol_>;

/** A finite number. Rejects NaN and +/-Infinity, which JSON turns into null. */
export const Finite = z.number().finite();

/**
 * A value that may legitimately be unknown.
 *
 * Missing data is the normal case with free tiers, not an error — a microcap
 * may have no EBITDA, a recent IPO no 12-month return. Modeling absence
 * explicitly as `null` keeps it distinguishable from a real zero, which is a
 * distinction that matters enormously in factor math.
 */
export const Maybe = <T extends z.ZodTypeAny>(schema: T) => schema.nullable();
